/**
 * SandboxSetupPopover — content for the Flow Designer sandbox toolbar button's
 * popover. While the backing instance is warming it shows the five-step
 * provisioning checklist plus a live "elapsed" timer; when provisioning failed
 * it shows the (sanitised) failure reason and a Retry action.
 *
 * Driven entirely by the already-polled SandboxEnvironmentState — no extra
 * fetch. `instanceStatus` is the raw `environment_instances.status`;
 * `createdAt` anchors the elapsed timer.
 */
import React from 'react';
import { Button, Heading, Text } from '@chatsdet/ui';
import { Cube, WarningCircle } from '@phosphor-icons/react';
import type { SandboxEnvironmentStatus } from '@chatsdet/types';
import { SandboxSetupChecklist } from './SandboxSetupChecklist';
import { useTickingNow, formatElapsed } from '../hooks/useTickingNow';

export interface SandboxSetupPopoverProps {
  status: SandboxEnvironmentStatus;
  /** Raw `environment_instances.status` driving the checklist step. */
  instanceStatus: string | null;
  /** Instance creation time — anchors the elapsed timer. */
  createdAt: string | null;
  /** Sanitised provisioning failure detail (failed only). */
  failureReason: string | null;
  /** Retry (re-ensure) the backing instance. */
  onStart: () => void;
  isEnsuring: boolean;
}

function ProvisioningElapsed({ createdAt }: { createdAt: string }) {
  const now = useTickingNow();
  const elapsed = formatElapsed(now - new Date(createdAt).getTime());
  return (
    <Text size="xs" color="muted">
      <span className="tabular-nums font-medium text-foreground">{elapsed}</span> elapsed
    </Text>
  );
}

export function SandboxSetupPopover({
  status,
  instanceStatus,
  createdAt,
  failureReason,
  onStart,
  isEnsuring,
}: SandboxSetupPopoverProps) {
  if (status === 'failed') {
    return (
      <div className="flex w-72 flex-col gap-3 p-3">
        <div className="flex items-center gap-2">
          <WarningCircle size={18} weight="fill" className="text-status-degraded" />
          <Heading level={5} className="text-sm">
            Sandbox provisioning failed
          </Heading>
        </div>
        <Text size="sm" color="muted">
          {failureReason ?? 'Provisioning failed. Retry, or contact support if it persists.'}
        </Text>
        <Button
          variant="primary"
          size="sm"
          isDisabled={isEnsuring}
          onPress={onStart}
          className="self-start"
        >
          {isEnsuring ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex w-72 flex-col gap-3 p-3">
      <div className="flex items-center gap-2.5">
        <div className="relative flex h-7 w-7 items-center justify-center rounded-md bg-primary">
          <Cube size={15} weight="fill" className="text-primary-foreground" />
          <span
            aria-hidden
            className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-zinc-900 animate-pulse"
          />
        </div>
        <div className="min-w-0">
          <Heading level={5} className="text-sm leading-tight">
            Setting up your sandbox
          </Heading>
          <Text size="xs" color="muted">
            Usually takes 2–3 minutes.
          </Text>
        </div>
      </div>
      {createdAt && <ProvisioningElapsed createdAt={createdAt} />}
      <SandboxSetupChecklist status={instanceStatus} size="sm" />
    </div>
  );
}

SandboxSetupPopover.displayName = 'SandboxSetupPopover';
